import { gql, useQuery } from '@apollo/client';
import { ClinicianFilter } from 'gql-types';
import { useEffect, useMemo, useState } from 'react';

import { useAppConfig } from '../../fetching/api/appConfig';
import { ScheduleChangeRequestIndicatorQueryQuery } from './useScheduleChangeRequestCounts.gql.types';

export const SCHEDULE_CHANGE_REQUEST_INDICATOR_QUERY = gql`
    query ScheduleChangeRequestIndicatorQuery(
        $pagination: PaginationInput!
        $filters: RequestToCreateShiftBlockFilter
    ) {
        scheduleChangeRequests {
            requestsToCreateShiftBlock(pagination: $pagination, filters: $filters) {
                items {
                    id
                    nextOccurrence {
                        startDatetime
                    }
                }
            }
        }
    }
`;

// Queries for schedule change requests that are pending review by the currently logged in user.
// Returns counts of all requests and those requests which are considered urgent because their start
// date is within the next 24 hours.
//
// We fetch lightweight items rather than just totalCount so we can client-side filter out requests
// where nextOccurrence is null (expired recurring requests the backend's mayHaveFutureOccurrences
// filter incorrectly includes). This keeps the badge count aligned with the results table, which
// applies the same filter.
export const useScheduleChangeRequestCounts = (requestingClinician?: ClinicianFilter) => {
    const {
        scheduling: { manage_shift_block_requests: manageShiftBlockRequests },
    } = useAppConfig();
    const [oneDayFromNow, setOneDayFromNow] = useState(new Date(Date.now() + 24 * 60 * 60 * 1000));

    useEffect(() => {
        if (!manageShiftBlockRequests) return;

        // Update the oneDayFromNow state every 5 minutes
        const timer = setTimeout(() => {
            setOneDayFromNow(new Date(Date.now() + 24 * 60 * 60 * 1000));
        }, 1000 * 60 * 5);

        return () => clearTimeout(timer);
    }, [manageShiftBlockRequests, setOneDayFromNow]);

    const { data } = useQuery<ScheduleChangeRequestIndicatorQueryQuery>(
        SCHEDULE_CHANGE_REQUEST_INDICATOR_QUERY,
        {
            variables: {
                pagination: { limit: 500, offset: 0 },
                filters: {
                    mayHaveFutureOccurrences: { exact: true },
                    requestingClinician,
                },
            },
            skip: !manageShiftBlockRequests,
        },
    );

    const { allRequestsCount, urgentRequestsCount } = useMemo(() => {
        const items = data?.scheduleChangeRequests.requestsToCreateShiftBlock?.items ?? [];
        let allCount = 0;
        let urgentCount = 0;
        for (const item of items) {
            if (item.nextOccurrence === null) continue;
            allCount++;
            if (new Date(item.nextOccurrence.startDatetime) < oneDayFromNow) {
                urgentCount++;
            }
        }
        return { allRequestsCount: allCount, urgentRequestsCount: urgentCount };
    }, [data, oneDayFromNow]);

    return { allRequestsCount, urgentRequestsCount };
};
